/**
 * RevPAR 前端直调 PMS businessReportList + 本地缓存 1 天
 * 不再向后端请求 RevPAR，避免后端打日志与调第三方
 */

import {
  getBusinessReportListFromThirdParty,
  type RevparBusinessReportItem,
  type RevPARData,
} from '../../../../../service/storeRevenueApi';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from '../../../utils/storeRevenueLocalStorage';

const REVPAR_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 天
const BUSINESS_TZ = 'Asia/Shanghai';

function formatTimestampToChinaDate(timestampSeconds: number): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(timestampSeconds * 1000));
}

function getChinaDateParts(timestampSeconds: number): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(timestampSeconds * 1000));
  const year = Number.parseInt(parts.find((p) => p.type === 'year')?.value ?? '0', 10);
  const month = Number.parseInt(parts.find((p) => p.type === 'month')?.value ?? '0', 10) - 1;
  const day = Number.parseInt(parts.find((p) => p.type === 'day')?.value ?? '0', 10);
  return { year, month, day };
}

interface RevparCacheEntry {
  data: RevPARData;
  fetchedAt: number;
}

const revparChartCache = new Map<string, RevparCacheEntry>();
const inFlightRevpar = new Map<string, Promise<RevPARData>>();

function hasRevparDataShape(v: unknown): v is RevPARData {
  if (v == null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return 'chartData' in o && o.chartData != null && typeof o.chartData === 'object';
}

/** 冷启动从 localStorage 恢复：若有有效数据（1 天内）则同步返回，供 hook 做初始渲染 */
export function getRevparFromLocalStorageSync(hotelId: number | string): RevPARData | null {
  const stored = getStoreFromLocalStorage(hotelId)?.revpar;
  if (!stored?.data || typeof stored.fetchedAt !== 'number') return null;
  if (Date.now() - stored.fetchedAt > REVPAR_CACHE_TTL_MS) return null;
  if (!hasRevparDataShape(stored.data)) return null;
  const data = stored.data;
  revparChartCache.set(cacheKey(hotelId), { data, fetchedAt: stored.fetchedAt });
  return data;
}

function cacheKey(hotelId: number | string): string {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `revpar:${hotelId}:${today}`;
}

function isValid(entry: RevparCacheEntry): boolean {
  return Date.now() - entry.fetchedAt < REVPAR_CACHE_TTL_MS;
}

const emptyChartData: RevPARData['chartData'] = {
  categories: [],
  dataset: { priceKlineData: [], RevPAR: [], RevPARLastYear: [] },
  axes: {
    left: { id: 'left', position: 'left', name: '可售房收入', unit: '元', min: 0, max: 100 },
  },
  series: [],
};

function buildRevparChartFromLists(
  thisYearList: RevparBusinessReportItem[],
  lastYearList: RevparBusinessReportItem[]
): RevPARData['chartData'] {
  const sortedThis = [...thisYearList].sort((a, b) => a.date - b.date);
  const sortedLast = [...lastYearList].sort((a, b) => a.date - b.date);
  const recentMonths = sortedThis.slice(-12);
  if (recentMonths.length === 0) return emptyChartData;

  const categories = recentMonths.map((item) => {
    const { month } = getChinaDateParts(item.date);
    return `${month + 1}月`;
  });
  const revparValues = recentMonths.map((item) => Math.round(item.roomRevpar ?? 0));
  const revparLastYear = recentMonths.map((thisYearItem) => {
    const { year, month } = getChinaDateParts(thisYearItem.date);
    const lastYearMatch = sortedLast.find((item) => {
      const ly = getChinaDateParts(item.date);
      return ly.month === month && ly.year === year - 1;
    });
    return lastYearMatch ? Math.round(lastYearMatch.roomRevpar ?? 0) : 0;
  });
  const priceKlineData = recentMonths.map((item) => {
    const adr = item.adr ?? 0;
    const variation = adr * 0.1;
    return [
      Math.round(adr - variation),
      Math.round(adr + variation),
      Math.round(adr - variation * 0.5),
      Math.round(adr + variation * 0.5),
    ];
  });
  const allRevparValues = [...revparValues, ...revparLastYear];
  const maxRevpar = Math.max(...allRevparValues, 1);
  const leftAxisMax = Math.ceil(maxRevpar * 1.2);

  return {
    categories,
    dataset: { priceKlineData, RevPAR: revparValues, RevPARLastYear: revparLastYear },
    axes: {
      left: {
        id: 'left',
        position: 'left',
        name: '可售房收入',
        unit: '元',
        min: 0,
        max: leftAxisMax,
      },
    },
    series: [
      { id: 'RevPAR', name: '可售房收入', dataKey: 'RevPAR', type: 'bar', axisId: 'left', color: '#5470c6' },
      { id: 'RevPARLastYear', name: '去年同期', dataKey: 'RevPARLastYear', type: 'line', axisId: 'left', color: '#91cc75', lineStyle: { type: 'dashed' } },
    ],
  };
}

function buildRevparChartFromListsDaily(
  thisYearList: RevparBusinessReportItem[],
  lastYearList: RevparBusinessReportItem[],
  dayCount: number
): RevPARData['chartData'] {
  const sortedThis = [...thisYearList].sort((a, b) => a.date - b.date);
  const sortedLast = [...lastYearList].sort((a, b) => a.date - b.date);
  const recentDays = sortedThis.slice(-dayCount);
  if (recentDays.length === 0) return emptyChartData;

  const categories = recentDays.map((item) => {
    const { month, day } = getChinaDateParts(item.date);
    return `${month + 1}/${day}`;
  });
  const revparValues = recentDays.map((item) => Math.round(item.roomRevpar ?? 0));
  const revparLastYear = recentDays.map((thisYearItem) => {
    const { year, month, day } = getChinaDateParts(thisYearItem.date);
    const lastYearMatch = sortedLast.find((item) => {
      const ly = getChinaDateParts(item.date);
      return ly.month === month && ly.year === year - 1 && ly.day === day;
    });
    return lastYearMatch ? Math.round(lastYearMatch.roomRevpar ?? 0) : 0;
  });
  const priceKlineData = recentDays.map((item) => {
    const adr = item.adr ?? 0;
    const variation = adr * 0.1;
    return [
      Math.round(adr - variation),
      Math.round(adr + variation),
      Math.round(adr - variation * 0.5),
      Math.round(adr + variation * 0.5),
    ];
  });
  const allRevparValues = [...revparValues, ...revparLastYear];
  const maxRevpar = Math.max(...allRevparValues, 1);
  const leftAxisMax = Math.ceil(maxRevpar * 1.2);

  return {
    categories,
    dataset: { priceKlineData, RevPAR: revparValues, RevPARLastYear: revparLastYear },
    axes: {
      left: {
        id: 'left',
        position: 'left',
        name: '可售房收入',
        unit: '元',
        min: 0,
        max: leftAxisMax,
      },
    },
    series: [
      { id: 'RevPAR', name: '可售房收入', dataKey: 'RevPAR', type: 'bar', axisId: 'left', color: '#5470c6' },
      { id: 'RevPARLastYear', name: '去年同期', dataKey: 'RevPARLastYear', type: 'line', axisId: 'left', color: '#91cc75', lineStyle: { type: 'dashed' } },
    ],
  };
}

const revparChartDailyCache = new Map<string, RevparCacheEntry>();
const inFlightRevparDaily = new Map<string, Promise<RevPARData>>();

function cacheKeyDaily(hotelId: number | string, days: number): string {
  const todayStr = formatTimestampToChinaDate(Math.floor(Date.now() / 1000));
  return `revpar:${hotelId}:daily:${days}:${todayStr}`;
}

export async function getRevparChartDailyWithCache(
  hotelId: number | string,
  days: 7 | 30
): Promise<RevPARData> {
  const key = cacheKeyDaily(hotelId, days);
  const entry = revparChartDailyCache.get(key);
  if (entry && isValid(entry)) return entry.data;

  const inFlight = inFlightRevparDaily.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<RevPARData> => {
    const now = Date.now();
    const endDateStr = formatTimestampToChinaDate(Math.floor(now / 1000));
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - days);
    const startDateStr = formatTimestampToChinaDate(Math.floor(startDate.getTime() / 1000));

    const lastYearEnd = new Date(now);
    lastYearEnd.setFullYear(lastYearEnd.getFullYear() - 1);
    const lastYearEndStr = formatTimestampToChinaDate(Math.floor(lastYearEnd.getTime() / 1000));
    const lastYearStart = new Date(startDate.getTime());
    lastYearStart.setFullYear(lastYearStart.getFullYear() - 1);
    const lastYearStartStr = formatTimestampToChinaDate(Math.floor(lastYearStart.getTime() / 1000));

    const [thisYearList, lastYearList] = await Promise.all([
      getBusinessReportListFromThirdParty(hotelId, { dateType: 0, startDate: startDateStr, endDate: endDateStr }),
      getBusinessReportListFromThirdParty(hotelId, { dateType: 0, startDate: lastYearStartStr, endDate: lastYearEndStr }),
    ]);
    const chartData = buildRevparChartFromListsDaily(thisYearList, lastYearList, days);
    const data: RevPARData = { chartData };
    revparChartDailyCache.set(key, { data, fetchedAt: Date.now() });
    return data;
  })().finally(() => { inFlightRevparDaily.delete(key); });
  inFlightRevparDaily.set(key, promise);
  return promise;
}

export async function getRevparChartWithCache(hotelId: number | string): Promise<RevPARData> {
  const key = cacheKey(hotelId);
  const entry = revparChartCache.get(key);
  if (entry && isValid(entry)) return entry.data;

  const fromLocal = getRevparFromLocalStorageSync(hotelId);
  if (fromLocal != null) return fromLocal;

  const inFlight = inFlightRevpar.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<RevPARData> => {
    const endDateObj = new Date();
    const startDateObj = new Date(endDateObj);
    startDateObj.setDate(startDateObj.getDate() - 365);
    const startDateStr = formatTimestampToChinaDate(Math.floor(startDateObj.getTime() / 1000));
    const endDateStr = formatTimestampToChinaDate(Math.floor(endDateObj.getTime() / 1000));

    const lastYearEnd = new Date(endDateObj);
    lastYearEnd.setFullYear(lastYearEnd.getFullYear() - 1);
    const lastYearStart = new Date(startDateObj);
    lastYearStart.setFullYear(lastYearStart.getFullYear() - 1);
    const lastYearStartStr = formatTimestampToChinaDate(Math.floor(lastYearStart.getTime() / 1000));
    const lastYearEndStr = formatTimestampToChinaDate(Math.floor(lastYearEnd.getTime() / 1000));

    const [thisYearList, lastYearList] = await Promise.all([
      getBusinessReportListFromThirdParty(hotelId, { dateType: 1, startDate: startDateStr, endDate: endDateStr }),
      getBusinessReportListFromThirdParty(hotelId, { dateType: 1, startDate: lastYearStartStr, endDate: lastYearEndStr }),
    ]);
    const chartData = buildRevparChartFromLists(thisYearList, lastYearList);
    const data: RevPARData = { chartData };
    const fetchedAt = Date.now();
    revparChartCache.set(key, { data, fetchedAt });
    setStoreSliceInLocalStorage(hotelId, 'revpar', { data, fetchedAt });
    return data;
  })().finally(() => { inFlightRevpar.delete(key); });
  inFlightRevpar.set(key, promise);
  return promise;
}

export function clearRevparCache(hotelId: number | string): void {
  const prefix = `revpar:${String(hotelId)}`;
  const keysToDelete = (map: Map<string, unknown>): string[] => {
    const out: string[] = [];
    map.forEach((_, key) => { if (key.startsWith(prefix)) out.push(key); });
    return out;
  };
  [...keysToDelete(revparChartCache), ...keysToDelete(revparChartDailyCache)].forEach((k) => {
    revparChartCache.delete(k);
    revparChartDailyCache.delete(k);
    inFlightRevpar.delete(k);
    inFlightRevparDaily.delete(k);
  });
}

export function __getRevparCacheForDev(): Map<string, RevparCacheEntry> {
  return revparChartCache;
}
